import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconArrowLeft, IconArrowsRightLeft, IconCalendar, IconCircleDot, IconPackages, IconPlus, IconRefresh } from '@tabler/icons-react';
import { Funnel } from 'lucide-react';
import {
  BoardToolbarAreaClickUpChip,
  BoardToolbarCoordenadorClickUpChip,
  BoardToolbarExpandSearch,
  BoardToolbarTecnicosClickUpChip,
} from '../../board/toolbar';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  EC_FILTER_POPOVER_CLASS,
  buildEcFilterPopoverStyle,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import {
  PeriodModal,
  getCalendarBounds,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { ymdToBr } from '../../relatorios/utils/relatoriosPeriodDateUtils';
import PermColumnFilterPopover from '../../users-customers/components/PermColumnFilterPopover';
import {
  viewTech,
} from '../data/kitTecnicoMockData';
import {
  fetchKits,
  fetchKitMovements,
  fetchKitPendingPickup,
  mapApiKitToLocal,
  mapKitMovements,
  mapPendingPickupOrders,
  retryKitErpQueueItem,
  updateKitQuantityMax,
} from '../services/kitTecnicoService';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import KitCreateKitModal from './KitCreateKitModal';
import { KitListSkeleton, KitPedidosSkeleton, KitTableSkeletonRows } from './KitTecnicoSkeleton';
import { ProductStockPicker, KitQtyMotivoPicker } from './KitCreateProductsStep';
import './KitTecnicoPage.css';
import './KitCreateProductsStep.css';
import './KitCreateKitModal.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';

function toggleSelection(prev, value) {
  return prev.includes(value) ? prev.filter((x) => x !== value) : prev.concat(value);
}

function KitStatusPills({ sitCor, sitLabel, temSobra, sobraCor, sobraLabel }) {
  return (
    <div className="kit-status-pills">
      <div
        className="kit-status-pill kit-tone-surface"
        style={{ '--kit-tone': sitCor, color: sitCor }}
      >
        <span className="kit-status-dot" style={{ background: sitCor }} />
        {sitLabel}
      </div>
      {temSobra ? (
        <div
          className="kit-status-pill kit-tone-surface"
          style={{ '--kit-tone': sobraCor, color: sobraCor }}
        >
          <span className="kit-status-dot" style={{ background: sobraCor }} />
          {sobraLabel}
        </div>
      ) : null}
    </div>
  );
}

function KitTechFace({ photoUrl, initials, tone, size = 'md', className = '' }) {
  const src = String(photoUrl || '').trim();
  const letters = String(initials || '?').trim() || '?';
  const sizeClass =
    size === 'lg' ? 'kit-avatar--lg' : size === 'sm' ? 'kit-avatar--sm' : size === 'xs' ? 'kit-avatar--xs' : '';
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={`kit-avatar kit-avatar--photo kit-avatar--round ${sizeClass} ${className}`.trim()}
        draggable={false}
      />
    );
  }
  return (
    <div
      className={`kit-avatar kit-avatar--round kit-tone-surface ${sizeClass} ${className}`.trim()}
      style={{ '--kit-tone': tone, color: tone }}
      aria-hidden
    >
      {letters}
    </div>
  );
}

function KitFabAnchorTooltip({ children, content, label }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);

  const show = useCallback(() => {
    const el = anchorRef.current;
    if (!el || !content) return;
    setRect(el.getBoundingClientRect());
    setOpen(true);
  }, [content]);

  const hide = useCallback(() => {
    setOpen(false);
    setRect(null);
  }, []);

  const alignEnd = rect ? rect.left + 520 > window.innerWidth - 12 : false;
  const tooltipStyle = rect
    ? alignEnd
      ? { top: rect.bottom + 8, left: rect.right, transform: 'translateX(-100%)' }
      : { top: rect.bottom + 8, left: rect.left }
    : null;

  return (
    <span
      ref={anchorRef}
      className="kit-fab-tip-wrap"
      aria-label={label}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && rect && content
        ? createPortal(
            <div
              className={`kit-fab-tip kit-fab-tip--bottom ${alignEnd ? 'kit-fab-tip--end' : 'kit-fab-tip--start'}`}
              style={tooltipStyle}
              role="tooltip"
            >
              {content}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

function KitFabChip({ techId, desvio }) {
  const items = Array.isArray(desvio.itens) ? desvio.itens : [];
  const chip = (
    <div className="kit-fab-chip kit-tone-surface" style={{ '--kit-tone': desvio.cor }}>
      <span className="kit-fab-name">{desvio.fab}</span>
      {desvio.pct ? <span className="kit-fab-pct">{desvio.pct}</span> : null}
      {desvio.delta ? <span className="kit-fab-delta">{desvio.delta}</span> : null}
    </div>
  );
  if (!items.length) return chip;

  const title = desvio.grouped ? 'Outros fabricantes' : desvio.fab;
  const content = (
    <>
      <p className="kit-fab-tip-title">{title}</p>
      <p className="kit-fab-tip-text">
        {desvio.grouped
          ? 'Produtos pontuados nestes fabricantes, com máximo do kit e saldo em estoque.'
          : 'Produtos pontuados nesta marca, com máximo do kit e saldo em estoque.'}
      </p>
      <div className="kit-fab-tip-list">
        {items.map((p, i) => (
          <div key={`${techId}-${p.nome}-${i}`} className="kit-fab-tip-row">
            <span className="kit-fab-tip-prod">
              {desvio.grouped && p.fab ? `${p.fab} · ${p.nome}` : p.nome}
            </span>
            <strong>Máx {p.kit} · Saldo {p.estoque}</strong>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <KitFabAnchorTooltip
      content={content}
      label={`${desvio.fab}: ${items.length} produto${items.length === 1 ? '' : 's'} pontuado${items.length === 1 ? '' : 's'}`}
    >
      {chip}
    </KitFabAnchorTooltip>
  );
}

function KitColetaPill({ qty, pedido, tone }) {
  const hostRef = useRef(null);
  const [tip, setTip] = useState(null);
  const amount = Math.max(0, Number(qty) || 0);
  const order = String(pedido || '').trim();
  const ready = amount > 0;

  const showTip = () => {
    if (!ready || !hostRef.current) return;
    const r = hostRef.current.getBoundingClientRect();
    const above = r.top > 72;
    setTip({
      left: r.left + r.width / 2,
      top: above ? r.top - 8 : r.bottom + 8,
      place: above ? 'above' : 'below',
    });
  };

  return (
    <div
      ref={hostRef}
      className="kit-coleta-pill kit-tone-surface"
      style={{ '--kit-tone': tone, color: tone }}
      tabIndex={ready ? 0 : undefined}
      aria-label={
        ready
          ? `Quantidade ${amount}${order ? `, pedido ${order}` : ''}`
          : 'Sem peças prontas para coleta'
      }
      onMouseEnter={showTip}
      onMouseLeave={() => setTip(null)}
      onFocus={showTip}
      onBlur={() => setTip(null)}
    >
      {ready ? (
        <>
          <span className="kit-coleta-pill-qty">{amount}</span>
          <span className="kit-coleta-pill-dot" aria-hidden>
            ·
          </span>
          <span className="kit-coleta-pill-order">{order}</span>
        </>
      ) : (
        '—'
      )}
      {tip
        ? createPortal(
            <div
              className={`kit-coleta-tip kit-coleta-tip--${tip.place}`}
              role="tooltip"
              style={{ top: tip.top, left: tip.left }}
            >
              <span className="kit-coleta-tip-col">
                <span className="kit-coleta-tip-lbl">Quantidade</span>
                <span className="kit-coleta-tip-val">{amount}</span>
              </span>
              <span className="kit-coleta-tip-sep" aria-hidden />
              <span className="kit-coleta-tip-col">
                <span className="kit-coleta-tip-lbl">Pedido</span>
                <span className="kit-coleta-tip-val">{order || '—'}</span>
              </span>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function movYmd(dataStr) {
  const m = String(dataStr || '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

function movInPeriod(dataStr, start, end) {
  if (!start || !end) return true;
  const ymd = movYmd(dataStr);
  return Boolean(ymd) && ymd >= start && ymd <= end;
}

function cmpText(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR', { sensitivity: 'base', numeric: true });
}

/**
 * Kit técnico — lista → detalhe + modal Criar Kit (técnicos sem kit / LCMY_GO_KIT).
 */
const KitTecnicoPage = () => {
  const [view, setView] = useState('lista');
  const [techId, setTechId] = useState(null);
  const [aba, setAba] = useState('pecas');
  const [busca, setBusca] = useState('');
  const [coordsSel, setCoordsSel] = useState([]);
  const [areasSel, setAreasSel] = useState([]);
  const [techsSel, setTechsSel] = useState([]);
  const [openFilter, setOpenFilter] = useState(null);
  const [statusKey, setStatusKey] = useState('');
  const [abertos, setAbertos] = useState({});
  const [modal, setModal] = useState(false);
  const [addProductsOpen, setAddProductsOpen] = useState(false);
  const [kits, setKits] = useState([]);
  const [kitsLoading, setKitsLoading] = useState(true);
  const [kitsError, setKitsError] = useState(null);
  const [qtyBaseline, setQtyBaseline] = useState({});
  /** Rascunho local de quantity_max — persiste só em "Salvar alterações". */
  const [qtyDraft, setQtyDraft] = useState({});
  const [savingQty, setSavingQty] = useState(false);
  const { Toaster, error: toastError } = useCxExpToast();
  const [erpRetrying, setErpRetrying] = useState(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailSort, setDetailSort] = useState({ key: '', dir: 'asc' });
  const [movPeriod, setMovPeriod] = useState({ startDate: '', endDate: '' });
  const [movPeriodOpen, setMovPeriodOpen] = useState(false);
  const [movPeriodRect, setMovPeriodRect] = useState(null);
  const [movProdutosSel, setMovProdutosSel] = useState([]);
  const [movProdOpen, setMovProdOpen] = useState(false);
  const [movProdSearch, setMovProdSearch] = useState('');
  const [movOrigemSel, setMovOrigemSel] = useState([]);
  const [movOrigemOpen, setMovOrigemOpen] = useState(false);
  const [movTiposSel, setMovTiposSel] = useState([]);
  const [tipoFilterOpen, setTipoFilterOpen] = useState(false);
  const movDayBtnRef = useRef(null);
  const movProdBtnRef = useRef(null);
  const movOrigemBtnRef = useRef(null);
  const tipoFilterBtnRef = useRef(null);
  const [coletaPeriod, setColetaPeriod] = useState({ startDate: '', endDate: '' });
  const [coletaPeriodOpen, setColetaPeriodOpen] = useState(false);
  const [coletaPeriodRect, setColetaPeriodRect] = useState(null);
  const [coletaProdutosSel, setColetaProdutosSel] = useState([]);
  const [coletaProdOpen, setColetaProdOpen] = useState(false);
  const [coletaProdSearch, setColetaProdSearch] = useState('');
  const [coletaStatusSel, setColetaStatusSel] = useState([]);
  const [coletaStatusOpen, setColetaStatusOpen] = useState(false);
  const coletaDayBtnRef = useRef(null);
  const coletaProdBtnRef = useRef(null);
  const coletaStatusBtnRef = useRef(null);
  const [pendingPedidos, setPendingPedidos] = useState([]);
  const [pendingPedidosLoading, setPendingPedidosLoading] = useState(false);
  const [pendingPedidosError, setPendingPedidosError] = useState(null);
  const [kitMovs, setKitMovs] = useState([]);
  const [kitMovsLoading, setKitMovsLoading] = useState(false);
  const [kitMovsError, setKitMovsError] = useState(null);
  /** @type {[Record<string, Array<{ companyCode: string, stockAvailable: number }>>, Function]} */
  const [stockByProduct, setStockByProduct] = useState({});
  /** @type {Record<string, 'return_stock'|'keep_with_tech'>} */
  const [decreaseReasonByProduct, setDecreaseReasonByProduct] = useState({});
  const [savePhase, setSavePhase] = useState('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [savePedidos, setSavePedidos] = useState([]);
  const [saveErrors, setSaveErrors] = useState([]);

  const [refreshTick, setRefreshTick] = useState(0);

  const handleErpRetry = useCallback(
    async (id) => {
      setErpRetrying(id);
      try {
        await retryKitErpQueueItem(techId, id);
        setRefreshTick((n) => n + 1);
      } catch (err) {
        toastError(err?.message || 'Erro ao reprocessar');
      } finally {
        setErpRetrying(null);
      }
    },
    [techId, toastError]
  );

  const loadKits = useCallback(async () => {
    setKitsLoading(true);
    setKitsError(null);
    try {
      const data = await fetchKits();
      const list = Array.isArray(data?.kits) ? data.kits.map(mapApiKitToLocal) : [];
      setKits(list);
      return list;
    } catch (err) {
      setKits([]);
      setKitsError(err?.message || 'Erro ao carregar kits');
      return [];
    } finally {
      setKitsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKits();
  }, [loadKits]);

  const openChange = useCallback((key) => (open) => {
    setOpenFilter(open ? key : null);
  }, []);

  const todos = useMemo(() => kits.map(viewTech), [kits]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return todos.filter((t) => {
      if (coordsSel.length && !coordsSel.includes(t.coord)) return false;
      if (areasSel.length && !areasSel.includes(t.area)) return false;
      if (techsSel.length && !techsSel.includes(t.nome)) return false;
      if (statusKey === 'repor' && t.faltando === 0) return false;
      if (statusKey === 'excesso' && !t.temSobra) return false;
      if (statusKey === 'completo' && (t.sit !== 'completo' || t.temSobra)) return false;
      if (statusKey && statusKey !== 'repor' && statusKey !== 'excesso' && statusKey !== 'completo' && t.sit !== statusKey) return false;
      if (q && !t.nome.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [todos, coordsSel, areasSel, techsSel, statusKey, busca]);

  const ordem = { zerado: 0, pendencias: 1, excesso: 2 };
  const atencao = filtrados
    .filter((t) => t.sit !== 'completo' || t.temSobra)
    .sort((a, b) => {
      const ra = a.sit !== 'completo' ? ordem[a.sit] : 2;
      const rb = b.sit !== 'completo' ? ordem[b.sit] : 2;
      return (ra - rb) || a.pct - b.pct;
    });
  const ok = filtrados.filter((t) => t.sit === 'completo' && !t.temSobra);
  const okColapsados = ok.filter((t) => !abertos[t.id]);
  const okAbertos = ok.filter((t) => abertos[t.id]);
  // Altura dos chips verticais alinhada à altura típica do card de atenção (~linha de card).
  const alturaOkMin = Math.round(ok.reduce((m, t) => Math.max(m, t.nome.length), 0) * 7.9) + 132;
  const alturaOkCard = Math.min(Math.max(alturaOkMin, 220), 240);

  const contagem = useMemo(() => {
    const c = { zerado: 0, completo: 0, excesso: 0, pendencias: 0 };
    todos.forEach((t) => {
      if (t.temSobra) c.excesso += 1;
      if (t.sit === 'completo' && t.temSobra) return;
      if (t.sit !== 'excesso') c[t.sit] = (c[t.sit] || 0) + 1;
    });
    return c;
  }, [todos]);

  const coordOptions = useMemo(() => [...new Set(todos.map((t) => t.coord))].sort(), [todos]);
  const areaOptions = useMemo(() => [...new Set(todos.map((t) => t.area))].sort(), [todos]);
  const techOptions = useMemo(() => [...new Set(todos.map((t) => t.nome))].sort(), [todos]);

  const chips = [
    {
      key: 'repor',
      label: 'Precisa repor',
      ponto: '#d98207',
      n: todos.filter((t) => t.faltando > 0).length,
    },
    { key: 'zerado', label: 'Kit zerado', ponto: '#d64533', n: contagem.zerado },
    { key: 'completo', label: 'Kit completo', ponto: '#12a06a', n: contagem.completo },
    { key: 'excesso', label: 'Kit com excesso', ponto: '#2d6df6', n: contagem.excesso },
    /* mesma bolinha de "Precisa repor" */
    { key: 'pendencias', label: 'Kit com pendências', ponto: '#d98207', n: contagem.pendencias },
  ].map((c) => ({
    ...c,
    ativo: statusKey === c.key,
  }));

  /* “Kit completo” / colunas vazias: sem “Requer atenção” fantasma; Kits OK expande à esquerda */
  const showAtencaoCol = atencao.length > 0;
  const showOkCol = ok.length > 0 || !showAtencaoCol;
  const colsMod =
    showAtencaoCol && ok.length > 0 ? '' : showAtencaoCol ? ' kit-cols--atencao-only' : ' kit-cols--ok-only';

  const detRaw = techId ? todos.find((t) => t.id === techId) : null;

  /** Produtos de pedidos liberados (st_kit_liberado) → pill na grade de peças. */
  const liberadoByProduct = useMemo(() => {
    const map = new Map();
    for (const pd of pendingPedidos) {
      if (!pd.readyForPickup && !pd.liberado) continue;
      for (const it of pd.itens || []) {
        const code = String(it.cod || '').trim();
        if (!code) continue;
        const prev = map.get(code) || { amount: 0, order: '' };
        prev.amount += Number(it.qtd) || 0;
        if (!prev.order) prev.order = String(pd.numero || '').trim();
        map.set(code, prev);
      }
    }
    return map;
  }, [pendingPedidos]);

  const det = useMemo(() => {
    if (!detRaw) return null;
    return {
      ...detRaw,
      itens: detRaw.itens.map((p) => {
        const pct = p.kit ? Math.round((p.estoque / p.kit) * 100) : 0;
        const falta = p.kit - p.estoque;
        const cor =
          p.estoque === 0 && p.kit > 0
            ? '#d64533'
            : falta > 0
              ? '#d98207'
              : pct > 100
                ? '#2d6df6'
                : '#12a06a';
        const code = String(p.cod || p.productCode || '').trim();
        const hit = code ? liberadoByProduct.get(code) : null;
        const prontas = Math.max(Number(p.prontas) || 0, hit?.amount || 0);
        const pedido = p.pedido || hit?.order || '';
        return {
          ...p,
          prontas,
          pedido,
          cor,
          corExcesso: '#93b4fb',
          barra: `${Math.min(100, pct)}%`,
          barraExcesso: pct > 100 ? `${Math.min(35, pct - 100)}%` : '0%',
          statusLabel: falta > 0 ? `faltam ${falta}` : falta < 0 ? `sobra ${-falta}` : 'completo',
          coletaLabel: prontas ? `${prontas} · ${pedido}` : '—',
          coletaCor: prontas ? '#3d6fd9' : '#9a9aa3',
        };
      }),
      movs: kitMovs,
      pedidos: pendingPedidos,
    };
  }, [detRaw, pendingPedidos, liberadoByProduct, kitMovs]);

  useEffect(() => {
    if (!techId || view !== 'detalhe') {
      setPendingPedidos([]);
      setPendingPedidosError(null);
      setPendingPedidosLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPendingPedidosLoading(true);
      setPendingPedidosError(null);
      try {
        const data = await fetchKitPendingPickup(techId);
        if (cancelled) return;
        setPendingPedidos(mapPendingPickupOrders(data?.orders || []));
      } catch (err) {
        if (cancelled) return;
        setPendingPedidos([]);
        setPendingPedidosError(err?.message || 'Erro ao carregar pedidos aguardando coleta');
      } finally {
        if (!cancelled) setPendingPedidosLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [techId, view, refreshTick]);

  useEffect(() => {
    if (!techId || view !== 'detalhe') {
      setKitMovs([]);
      setKitMovsError(null);
      setKitMovsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setKitMovsLoading(true);
      setKitMovsError(null);
      try {
        const data = await fetchKitMovements(techId);
        if (cancelled) return;
        setKitMovs(mapKitMovements(data?.movements || []));
      } catch (err) {
        if (cancelled) return;
        setKitMovs([]);
        setKitMovsError(err?.message || 'Erro ao carregar movimentações');
      } finally {
        if (!cancelled) setKitMovsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [techId, view, refreshTick]);

  useEffect(() => {
    setDetailSort({ key: '', dir: 'asc' });
    setTipoFilterOpen(false);
    setMovPeriodOpen(false);
    setMovProdOpen(false);
    setMovOrigemOpen(false);
    setColetaPeriodOpen(false);
    setColetaProdOpen(false);
    setColetaStatusOpen(false);
  }, [aba]);

  useEffect(() => {
    setDetailSearch('');
    setDetailSort({ key: '', dir: 'asc' });
    setMovPeriod({ startDate: '', endDate: '' });
    setMovProdutosSel([]);
    setMovOrigemSel([]);
    setMovTiposSel([]);
    setMovPeriodOpen(false);
    setMovProdOpen(false);
    setMovOrigemOpen(false);
    setMovProdSearch('');
    setColetaPeriod({ startDate: '', endDate: '' });
    setColetaProdutosSel([]);
    setColetaStatusSel([]);
    setColetaPeriodOpen(false);
    setColetaProdOpen(false);
    setColetaStatusOpen(false);
    setColetaProdSearch('');
    setTipoFilterOpen(false);
    setQtyDraft({});
    setStockByProduct({});
    setDecreaseReasonByProduct({});
    setSavePhase('idle');
    setSaveMessage('');
    setSavePedidos([]);
    setSaveErrors([]);
  }, [techId]);

  const toggleDetailSort = useCallback((key) => {
    setDetailSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: '', dir: 'asc' };
    });
  }, []);

  const pecasView = useMemo(() => {
    if (!det) return [];
    const q = detailSearch.trim().toLowerCase();
    let rows = det.itens;
    if (q) {
      rows = rows.filter((p) =>
        [p.nome, p.cod, p.ref, p.fab, p.statusLabel, p.coletaLabel, String(p.kit), String(p.estoque)]
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    if (!detailSort.key) return rows;
    const mult = detailSort.dir === 'desc' ? -1 : 1;
    const get = (p) => {
      switch (detailSort.key) {
        case 'nome':
          return p.nome;
        case 'fab':
          return p.fab;
        case 'kit':
          return p.kit;
        case 'estoque':
          return p.estoque;
        case 'coleta':
          return p.prontas || 0;
        case 'saude':
          return p.kit ? p.estoque / p.kit : 0;
        default:
          return '';
      }
    };
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
      return cmpText(va, vb) * mult;
    });
  }, [det, detailSearch, detailSort]);

  const movsView = useMemo(() => {
    if (!det) return [];
    const q = detailSearch.trim().toLowerCase();
    let rows = det.movs;
    if (movPeriod.startDate && movPeriod.endDate) {
      rows = rows.filter((m) => movInPeriod(m.data, movPeriod.startDate, movPeriod.endDate));
    }
    if (movProdutosSel.length) rows = rows.filter((m) => movProdutosSel.includes(String(m.cod || '')));
    if (movOrigemSel.length) rows = rows.filter((m) => movOrigemSel.includes(m.linha2));
    if (movTiposSel.length) rows = rows.filter((m) => movTiposSel.includes(m.tipo));
    if (q) {
      rows = rows.filter((m) =>
        [
          m.tipo,
          m.nome,
          m.cod,
          m.ref,
          m.fab,
          m.qtdLabel,
          String(m.balanceAfter ?? ''),
          m.linha1,
          m.linha2,
          m.operacao,
          m.data,
        ]
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    return rows;
  }, [det, detailSearch, movPeriod, movProdutosSel, movOrigemSel, movTiposSel]);

  const pedidosView = useMemo(() => {
    if (!det) return [];
    const q = detailSearch.trim().toLowerCase();
    let rows = det.pedidos;
    if (coletaPeriod.startDate && coletaPeriod.endDate) {
      rows = rows.filter((pd) => movInPeriod(pd.data, coletaPeriod.startDate, coletaPeriod.endDate));
    }
    if (coletaProdutosSel.length) {
      rows = rows.filter((pd) =>
        (pd.itens || []).some((i) => coletaProdutosSel.includes(String(i.cod || '')))
      );
    }
    if (coletaStatusSel.length) {
      rows = rows.filter((pd) => coletaStatusSel.includes(pd.status));
    }
    if (q) {
      rows = rows.filter((pd) => {
        const itensTxt = (pd.itens || [])
          .map((i) => [i.nome, i.cod, i.ref, i.fab, i.qtd].join(' '))
          .join(' ');
        return [pd.numero, pd.status, pd.local, pd.data, itensTxt].join(' ').toLowerCase().includes(q);
      });
    }
    return rows;
  }, [det, detailSearch, coletaPeriod, coletaProdutosSel, coletaStatusSel]);

  const movTipoOptions = useMemo(() => {
    if (!det) return [];
    return [...new Set(det.movs.map((m) => m.tipo))].sort(cmpText);
  }, [det]);

  const movProductItems = useMemo(() => {
    const map = new Map();
    for (const m of kitMovs) {
      const cod = String(m.cod || '').trim();
      if (!cod || map.has(cod)) continue;
      const fab = String(m.fab || '').trim();
      map.set(cod, {
        value: cod,
        label: m.nome || cod,
        cod,
        ref: String(m.ref || '').trim(),
        fab: fab === '—' ? '' : fab,
      });
    }
    return Array.from(map.values()).sort((a, b) => cmpText(a.label, b.label));
  }, [kitMovs]);

  const movProductItemsFiltered = useMemo(() => {
    const t = movProdSearch.trim().toLowerCase();
    if (!t) return movProductItems;
    return movProductItems.filter((i) =>
      [i.label, i.cod, i.ref, i.fab].join(' ').toLowerCase().includes(t)
    );
  }, [movProductItems, movProdSearch]);

  const movOrigemItems = useMemo(() => {
    const seen = new Set();
    for (const m of kitMovs) {
      if (m.linha2 === 'OS' || m.linha2 === 'Pedido') seen.add(m.linha2);
    }
    return ['OS', 'Pedido'].filter((v) => seen.has(v)).map((v) => ({ value: v, label: v }));
  }, [kitMovs]);

  const movProdLayout = useEcFilterPopoverLayout(movProdBtnRef, movProdOpen, 460, 360);
  const movProdPopoverStyle = buildEcFilterPopoverStyle(movProdLayout);
  const movOrigemLayout = useEcFilterPopoverLayout(movOrigemBtnRef, movOrigemOpen, 238, 240);
  const movOrigemPopoverStyle = buildEcFilterPopoverStyle(movOrigemLayout);
  const showMovBalance = movProdutosSel.length > 0;
  const movGridClass = `kit-table-grid-movs${showMovBalance ? ' kit-table-grid-movs--balance' : ''}`;
  const movPeriodBounds = getCalendarBounds();
  const movPeriodHasSel = Boolean(movPeriod.startDate && movPeriod.endDate);
  const movPeriodLabel = !movPeriodHasSel
    ? null
    : movPeriod.startDate === movPeriod.endDate
      ? ymdToBr(movPeriod.startDate)
      : `${ymdToBr(movPeriod.startDate)} — ${ymdToBr(movPeriod.endDate)}`;
  const movProdChipValue =
    movProdutosSel.length === 1
      ? movProductItems.find((i) => i.value === movProdutosSel[0])?.label || '1'
      : movProdutosSel.length > 0
        ? String(movProdutosSel.length)
        : null;
  const movOrigemChipValue =
    movOrigemSel.length === 1 ? movOrigemSel[0] : movOrigemSel.length > 0 ? String(movOrigemSel.length) : null;

  const coletaProductItems = useMemo(() => {
    const map = new Map();
    for (const pd of pendingPedidos) {
      for (const i of pd.itens || []) {
        const cod = String(i.cod || '').trim();
        if (!cod || map.has(cod)) continue;
        const fab = String(i.fab || '').trim();
        map.set(cod, {
          value: cod,
          label: i.nome || cod,
          cod,
          ref: String(i.ref || '').trim(),
          fab: fab === '—' ? '' : fab,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => cmpText(a.label, b.label));
  }, [pendingPedidos]);

  const coletaProductItemsFiltered = useMemo(() => {
    const t = coletaProdSearch.trim().toLowerCase();
    if (!t) return coletaProductItems;
    return coletaProductItems.filter((i) =>
      [i.label, i.cod, i.ref, i.fab].join(' ').toLowerCase().includes(t)
    );
  }, [coletaProductItems, coletaProdSearch]);

  const coletaStatusItems = useMemo(() => {
    const seen = new Set();
    for (const pd of pendingPedidos) {
      const st = String(pd.status || '').trim();
      if (st) seen.add(st);
    }
    return [...seen].sort(cmpText).map((v) => ({ value: v, label: v }));
  }, [pendingPedidos]);

  const coletaProdLayout = useEcFilterPopoverLayout(coletaProdBtnRef, coletaProdOpen, 460, 360);
  const coletaProdPopoverStyle = buildEcFilterPopoverStyle(coletaProdLayout);
  const coletaStatusLayout = useEcFilterPopoverLayout(coletaStatusBtnRef, coletaStatusOpen, 320, 280);
  const coletaStatusPopoverStyle = buildEcFilterPopoverStyle(coletaStatusLayout);
  const coletaPeriodBounds = getCalendarBounds();
  const coletaPeriodHasSel = Boolean(coletaPeriod.startDate && coletaPeriod.endDate);
  const coletaPeriodLabel = !coletaPeriodHasSel
    ? null
    : coletaPeriod.startDate === coletaPeriod.endDate
      ? ymdToBr(coletaPeriod.startDate)
      : `${ymdToBr(coletaPeriod.startDate)} — ${ymdToBr(coletaPeriod.endDate)}`;
  const coletaProdChipValue =
    coletaProdutosSel.length === 1
      ? coletaProductItems.find((i) => i.value === coletaProdutosSel[0])?.label || '1'
      : coletaProdutosSel.length > 0
        ? String(coletaProdutosSel.length)
        : null;
  const coletaStatusChipValue =
    coletaStatusSel.length === 1
      ? coletaStatusSel[0]
      : coletaStatusSel.length > 0
        ? String(coletaStatusSel.length)
        : null;

  useEffect(() => {
    if (!movProdOpen) {
      setMovProdSearch('');
      return undefined;
    }
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-mov-prod-popover]');
      if (pop?.contains(e.target) || movProdBtnRef.current?.contains(e.target)) return;
      setMovProdOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [movProdOpen]);

  useEffect(() => {
    if (!movOrigemOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-mov-origem-popover]');
      if (pop?.contains(e.target) || movOrigemBtnRef.current?.contains(e.target)) return;
      setMovOrigemOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [movOrigemOpen]);

  useEffect(() => {
    if (!coletaProdOpen) {
      setColetaProdSearch('');
      return undefined;
    }
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-coleta-prod-popover]');
      if (pop?.contains(e.target) || coletaProdBtnRef.current?.contains(e.target)) return;
      setColetaProdOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [coletaProdOpen]);

  useEffect(() => {
    if (!coletaStatusOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-coleta-status-popover]');
      if (pop?.contains(e.target) || coletaStatusBtnRef.current?.contains(e.target)) return;
      setColetaStatusOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [coletaStatusOpen]);

  const abas = [
    { key: 'pecas', label: 'Peças do Kit', n: det ? pecasView.length : 0 },
    { key: 'movs', label: 'Movimentações', n: det ? movsView.length : 0 },
    { key: 'coletas', label: 'Aguardando coleta', n: det ? pedidosView.length : 0 },
  ];

  const voltarLista = () => {
    setView('lista');
    setTechId(null);
  };

  const sortInd = (key) => {
    if (detailSort.key !== key) return null;
    return (
      <span className="kit-th-sort-ind" aria-hidden="true">
        {detailSort.dir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const abrirTech = (id) => {
    setView('detalhe');
    setTechId(id);
    setAba('pecas');
    const tech = kits.find((t) => t.id === id);
    const mapped = viewTech(tech || { itens: [] });
    const base = {};
    for (const p of mapped.itens || []) {
      if (p?.cod != null) base[String(p.cod)] = Number(p.kit) || 0;
    }
    setQtyBaseline(base);
    setQtyDraft({});
    setStockByProduct({});
    setDecreaseReasonByProduct({});
  };

  const toggleOk = (id) => {
    setAbertos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const resolveDraftKit = useCallback(
    (cod, savedKit) => {
      const code = String(cod || '').trim();
      if (!code) return Math.max(0, Number(savedKit) || 0);
      if (qtyDraft[code] != null) return Math.max(0, Number(qtyDraft[code]) || 0);
      if (qtyBaseline[code] != null) return Math.max(0, Number(qtyBaseline[code]) || 0);
      return Math.max(0, Number(savedKit) || 0);
    },
    [qtyDraft, qtyBaseline]
  );

  const ajustar = (cod, delta) => {
    const code = String(cod || '').trim();
    if (!code || !techId) return;
    const item = (detRaw?.itens || []).find((p) => String(p.cod || '').trim() === code);
    const saved = qtyBaseline[code] ?? Number(item?.kit) ?? 0;
    setQtyDraft((prev) => {
      const current = prev[code] != null ? Number(prev[code]) : saved;
      const next = Math.max(0, current + delta);
      if (next === Number(saved)) {
        if (!(code in prev)) return prev;
        const { [code]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [code]: next };
    });
  };

  const qtyDirty = useMemo(() => {
    return Object.keys(qtyDraft).some((cod) => {
      const base = qtyBaseline[cod];
      if (base == null) return false;
      return Number(qtyDraft[cod]) !== Number(base);
    });
  }, [qtyDraft, qtyBaseline]);

  const dirtyProductCodes = useMemo(() => {
    const s = new Set();
    for (const cod of Object.keys(qtyDraft)) {
      const base = qtyBaseline[cod];
      if (base == null) continue;
      if (Number(qtyDraft[cod]) !== Number(base)) s.add(cod);
    }
    return s;
  }, [qtyDraft, qtyBaseline]);

  /** Aumentos de quantity_max → exigem empresa de estoque + geram pedido. */
  const increasedProductCodes = useMemo(() => {
    const s = new Set();
    for (const cod of Object.keys(qtyDraft)) {
      const base = qtyBaseline[cod];
      if (base == null) continue;
      if (Number(qtyDraft[cod]) > Number(base)) s.add(cod);
    }
    return s;
  }, [qtyDraft, qtyBaseline]);

  /** Diminuições → exigem motivo (+ empresa se devolver) + geram rollback de entrada. */
  const decreasedProductCodes = useMemo(() => {
    const s = new Set();
    for (const cod of Object.keys(qtyDraft)) {
      const base = qtyBaseline[cod];
      if (base == null) continue;
      if (Number(qtyDraft[cod]) < Number(base)) s.add(cod);
    }
    return s;
  }, [qtyDraft, qtyBaseline]);

  const stockPickCodes = useMemo(() => new Set(increasedProductCodes), [increasedProductCodes]);

  const showStockCol =
    increasedProductCodes.size > 0 || decreasedProductCodes.size > 0;

  const allIncreaseStocksOk = useMemo(() => {
    for (const cod of increasedProductCodes) {
      const list = stockByProduct[cod];
      if (!Array.isArray(list) || !list.length) return false;
    }
    return true;
  }, [increasedProductCodes, stockByProduct]);

  const allDecreaseMotivosOk = useMemo(() => {
    for (const cod of decreasedProductCodes) {
      const reason = decreaseReasonByProduct[cod];
      if (reason !== 'return_stock' && reason !== 'keep_with_tech') return false;
      if (reason === 'return_stock') {
        const list = stockByProduct[cod];
        if (!Array.isArray(list) || !list.length) return false;
      }
    }
    return true;
  }, [decreasedProductCodes, decreaseReasonByProduct, stockByProduct]);

  useEffect(() => {
    setStockByProduct((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const cod of Object.keys(next)) {
        const base = qtyBaseline[cod];
        const draft = qtyDraft[cod];
        if (base == null || draft == null || Number(draft) === Number(base)) {
          delete next[cod];
          changed = true;
        } else if (Number(draft) > Number(base) && !increasedProductCodes.has(cod)) {
          delete next[cod];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setDecreaseReasonByProduct((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const cod of Object.keys(next)) {
        if (!decreasedProductCodes.has(cod)) {
          delete next[cod];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [qtyDraft, qtyBaseline, increasedProductCodes, decreasedProductCodes]);

  const canSaveQty =
    qtyDirty &&
    !savingQty &&
    allIncreaseStocksOk &&
    allDecreaseMotivosOk;

  const setProductStock = useCallback((productCode, next) => {
    const code = String(productCode || '').trim();
    if (!code) return;
    setStockByProduct((prev) => ({
      ...prev,
      [code]: Array.isArray(next) ? next : [],
    }));
  }, []);

  const setProductDecreaseReason = useCallback((productCode, reason) => {
    const code = String(productCode || '').trim();
    if (!code) return;
    setDecreaseReasonByProduct((prev) => {
      if (!reason) {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      }
      return { ...prev, [code]: reason };
    });
  }, []);

  const refreshTechDetail = useCallback(async () => {
    const list = await loadKits();
    if (techId) {
      const tech = list.find((t) => t.id === techId);
      const mapped = viewTech(tech || { itens: [] });
      const base = {};
      for (const p of mapped.itens || []) {
        if (p?.cod != null) base[String(p.cod)] = Number(p.kit) || 0;
      }
      setQtyBaseline(base);
      setQtyDraft({});
    }
    setRefreshTick((n) => n + 1);
  }, [loadKits, techId]);

  const handleSaveQtyMax = async () => {
    if (!detRaw || !canSaveQty) return;
    const items = [...dirtyProductCodes]
      .map((productCode) => {
        const quantityMax = Number(qtyDraft[productCode]);
        const base = Number(qtyBaseline[productCode]);
        const payload = { productCode, quantityMax };
        if (Number.isFinite(base) && quantityMax !== base) {
          if (quantityMax > base) {
            payload.stock_sources = Array.isArray(stockByProduct[productCode])
              ? stockByProduct[productCode].map((s) => ({
                  companyCode: String(s.companyCode || '').trim(),
                  stockAvailable: Number(s.stockAvailable) || 0,
                }))
              : [];
          } else if (quantityMax < base) {
            const reason = decreaseReasonByProduct[productCode];
            payload.decrease_reason = reason;
            if (reason === 'return_stock') {
              payload.stock_sources = Array.isArray(stockByProduct[productCode])
                ? stockByProduct[productCode].map((s) => ({
                    companyCode: String(s.companyCode || '').trim(),
                    stockAvailable: Number(s.stockAvailable) || 0,
                  }))
                : [];
            }
          }
        }
        return payload;
      });
    if (!items.length) return;

    setSavingQty(true);
    setSavePhase('saving');
    setSaveMessage('');
    setSavePedidos([]);
    setSaveErrors([]);
    try {
      const data = await updateKitQuantityMax(detRaw.codtech || detRaw.id, items);
      setQtyDraft({});
      setStockByProduct({});
      setDecreaseReasonByProduct({});
      await refreshTechDetail();

      const pedidosList = Array.isArray(data?.pedidos) ? data.pedidos : [];
      const entradasList = Array.isArray(data?.entradas) ? data.entradas : [];
      const docsList = pedidosList.concat(entradasList);
      const pedidoNums = pedidosList
        .map((p) => String(p?.pedidoGerado || '').trim())
        .filter(Boolean);
      const entradaNums = entradasList
        .map((p) => String(p?.pedidoGerado || '').trim())
        .filter(Boolean);
      setSavePedidos(docsList);
      setSaveErrors([]);

      const nUpd = items.length;
      const baseMsg =
        nUpd === 1
          ? 'Quantidade máxima atualizada.'
          : `${nUpd} quantidades máximas atualizadas.`;
      const extra = [];
      if (pedidoNums.length) {
        extra.push(
          `${pedidoNums.length} ${pedidoNums.length === 1 ? 'pedido gerado' : 'pedidos gerados'}`
        );
      } else if (pedidosList.length) {
        extra.push('Pedidos processados');
      }
      if (entradaNums.length) {
        extra.push(
          `${entradaNums.length} ${
            entradaNums.length === 1 ? 'entrada gerada' : 'entradas geradas'
          } (rollback)`
        );
      } else if (entradasList.length) {
        extra.push('Rollback de entrada processado');
      }
      setSaveMessage(extra.length ? `${baseMsg} ${extra.join('. ')}.` : baseMsg);
      setSavePhase('success');

      if (techId && view === 'detalhe') {
        try {
          const pend = await fetchKitPendingPickup(techId);
          setPendingPedidos(mapPendingPickupOrders(pend?.orders || []));
        } catch {
          /* mantém lista anterior */
        }
      }
    } catch (err) {
      setSavePedidos([]);
      setSaveErrors(Array.isArray(err?.errors) ? err.errors : []);
      setSaveMessage(err?.message || 'Erro ao salvar quantidade máxima');
      setSavePhase('error');
    } finally {
      setSavingQty(false);
    }
  };

  const dismissSaveResult = useCallback(() => {
    setSavePhase('idle');
    setSaveMessage('');
    setSavePedidos([]);
    setSaveErrors([]);
  }, []);

  const handleKitsCreated = useCallback(() => {
    loadKits();
  }, [loadKits]);

  const handleProductsAdded = useCallback(() => {
    refreshTechDetail();
  }, [refreshTechDetail]);

  return (
    <>
      <header className="kit-header">
        <nav className="kit-breadcrumb" aria-label="Localização em estoque">
          <span className="kit-breadcrumb-avatar" aria-hidden="true">
            E
          </span>

          <span className="kit-breadcrumb-segment kit-breadcrumb-segment--parent">Estoque</span>
          <span className="kit-breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <span className="kit-breadcrumb-current" aria-current="page">
            <IconPackages size={13} stroke={1.75} aria-hidden className="kit-breadcrumb-current-icon" />
            <span className="kit-breadcrumb-current-text">Kit técnico</span>
          </span>
        </nav>
        {view === 'detalhe' ? (
          <div className="kit-detail-header-actions">
            <button
              type="button"
              className={`kit-refresh-btn${kitsLoading || pendingPedidosLoading ? ' is-refreshing' : ''}`}
              aria-label="Atualizar dados do técnico"
              title="Atualizar"
              disabled={kitsLoading || pendingPedidosLoading}
              onClick={() => refreshTechDetail()}
            >
              <IconRefresh size={15} stroke={1.75} aria-hidden />
            </button>
            <div className="board-clickup-filter-chip-anchor kit-detail-back-chip">
              <button
                type="button"
                className="board-clickup-filter-chip"
                aria-label="Voltar para listagem"
                onClick={voltarLista}
              >
                <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                  <IconArrowLeft size={13} stroke={1.5} />
                </span>
                <span className="board-clickup-filter-chip-text">
                  <span className="board-clickup-filter-chip-label kit-detail-back-label">Voltar</span>
                </span>
              </button>
            </div>
          </div>
        ) : null}
      </header>

      {view === 'lista' ? (
        <div className="filters-bar kit-filters-bar">
          <div className="filter-buttons">
            <div className="filter-group board-toolbar-filter-group">
              <BoardToolbarCoordenadorClickUpChip
                options={coordOptions}
                selected={coordsSel}
                isOpen={openFilter === 'coordenador'}
                onOpenChange={openChange('coordenador')}
                onToggleItem={(v) => setCoordsSel((prev) => toggleSelection(prev, v))}
                onToggleAll={(keys, allMarked) => {
                  setCoordsSel((prev) =>
                    allMarked
                      ? prev.filter((x) => !keys.includes(x))
                      : Array.from(new Set([...prev, ...keys]))
                  );
                }}
                onClear={() => setCoordsSel([])}
                onApply={() => setOpenFilter(null)}
              />
              <BoardToolbarAreaClickUpChip
                options={areaOptions}
                selected={areasSel}
                isOpen={openFilter === 'area'}
                onOpenChange={openChange('area')}
                onToggleItem={(v) => setAreasSel((prev) => toggleSelection(prev, v))}
                onToggleAll={(keys, allMarked) => {
                  setAreasSel((prev) =>
                    allMarked
                      ? prev.filter((x) => !keys.includes(x))
                      : Array.from(new Set([...prev, ...keys]))
                  );
                }}
                onClear={() => setAreasSel([])}
                onApply={() => setOpenFilter(null)}
              />
              <BoardToolbarTecnicosClickUpChip
                options={techOptions}
                selected={techsSel}
                isOpen={openFilter === 'tecnico'}
                onOpenChange={openChange('tecnico')}
                onToggleItem={(v) => setTechsSel((prev) => toggleSelection(prev, v))}
                onToggleAll={(keys, allMarked) => {
                  setTechsSel((prev) =>
                    allMarked
                      ? prev.filter((x) => !keys.includes(x))
                      : Array.from(new Set([...prev, ...keys]))
                  );
                }}
                onClear={() => setTechsSel([])}
                onApply={() => setOpenFilter(null)}
              />
              <span className="board-toolbar-separator" aria-hidden="true">
                |
              </span>
              <div className="board-toolbar-expand-search-wrap">
                <BoardToolbarExpandSearch
                  value={busca}
                  onChange={setBusca}
                  placeholder="Buscar técnico…"
                  ariaLabel="Buscar técnico"
                />
              </div>
            </div>

            <div className="search-container board-toolbar-right">
              <div className="board-toolbar-actions-group kit-filters-actions">
                <div className="kit-status-seg" role="group" aria-label="Situação do kit">
                  {chips.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`kit-status-seg-btn${c.ativo ? ' is-on' : ''}`}
                      aria-pressed={c.ativo}
                      aria-label={`${c.label}: ${c.n}`}
                      onClick={() => setStatusKey((prev) => (prev === c.key ? '' : c.key))}
                    >
                      <span
                        className="kit-status-seg-dot"
                        style={{ background: c.ponto }}
                        aria-hidden="true"
                      />
                      <span className="kit-status-seg-label">{c.label}</span>
                      <span className="kit-status-seg-n">{c.n}</span>
                    </button>
                  ))}
                </div>
                <span className="board-toolbar-separator" aria-hidden="true">
                  |
                </span>
                <button
                  type="button"
                  className={`kit-refresh-btn${kitsLoading ? ' is-refreshing' : ''}`}
                  aria-label="Atualizar kits"
                  title="Atualizar"
                  disabled={kitsLoading}
                  onClick={() => loadKits()}
                >
                  <IconRefresh size={15} stroke={1.75} aria-hidden />
                </button>
                <button
                  type="button"
                  className="kit-create-kit-btn"
                  aria-label="Criar Kit"
                  onClick={() => setModal(true)}
                >
                  <span className="kit-create-kit-btn-plus" aria-hidden="true">
                    +
                  </span>
                  Criar Kit
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={`kit-tecnico-page${view === 'lista' ? ' has-filters' : ''}`}
        data-module="kit-tecnico"
      >
      <div className="kit-root">
        {view === 'lista' && (
          <div className="kit-list-shell">
            {kitsLoading ? (
              <KitListSkeleton />
            ) : kitsError ? (
              <p className="kit-list-status kit-list-status--error">{kitsError}</p>
            ) : !todos.length ? (
              <p className="kit-list-status">Nenhum técnico com kit registrado.</p>
            ) : (
            <div className={`kit-cols${colsMod}`}>
              {showAtencaoCol ? (
              <div className="kit-col-atencao">
                <div className="kit-section-head">
                  <div className="kit-section-title">Requer atenção</div>
                  <div className="kit-section-badge kit-section-badge--warn">{atencao.length}</div>
                  <div className="kit-section-line" />
                </div>
                <div className="kit-card-grid">
                  {atencao.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="kit-card"
                      onClick={() => abrirTech(t.id)}
                    >
                      <div className="kit-card-top">
                        <KitTechFace
                          photoUrl={t.profilePhotoUrl}
                          initials={t.iniciais}
                          tone={t.avatarCor}
                        />
                        <div className="kit-card-id">
                          <div className="kit-card-name">{t.nome}</div>
                          <div className="kit-card-sub">{t.linha2}</div>
                        </div>
                        <KitStatusPills
                          sitCor={t.sitCor}
                          sitLabel={t.sitLabel}
                          temSobra={t.temSobra}
                          sobraCor={t.sobraCor}
                          sobraLabel={t.sobraLabel}
                        />
                      </div>
                      <div>
                        <div className="kit-metric-row">
                          <div className="kit-metric-left">
                            <span className="kit-metric-big">{t.emEstoque}</span>
                            <span className="kit-metric-den">/ {t.totalKit}</span>
                            <span className="kit-metric-lbl">peças</span>
                          </div>
                          <div className="kit-metric-pct" style={{ color: t.sitCor }}>
                            {t.pctLabel}
                          </div>
                        </div>
                        <div className="kit-bar">
                          <div className="kit-bar-fill" style={{ width: t.barra, background: t.sitCor }} />
                          <div
                            className="kit-bar-fill"
                            style={{ width: t.barraExcesso, background: t.corExcesso }}
                          />
                        </div>
                      </div>
                      {t.temDesvios ? (
                        <div className="kit-fabs">
                          {t.desvios.map((d) => (
                            <KitFabChip key={`${t.id}-${d.fab}`} techId={t.id} desvio={d} />
                          ))}
                        </div>
                      ) : null}
                      <div className="kit-card-foot">
                        <div className="kit-foot-col">
                          <div className="kit-foot-lbl">Última coleta</div>
                          <div className="kit-foot-val">{t.ultimaColeta}</div>
                        </div>
                        <div className="kit-foot-col">
                          <div className="kit-foot-lbl">Último uso</div>
                          <div className="kit-foot-val">{t.ultimoUso}</div>
                        </div>
                      </div>
                      {t.temColetas ? (
                        <div
                          className="kit-coleta-banner"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setView('detalhe');
                            setTechId(t.id);
                            setAba('coletas');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              setView('detalhe');
                              setTechId(t.id);
                              setAba('coletas');
                            }
                          }}
                        >
                          <span className="kit-coleta-dot" />
                          <span className="kit-coleta-txt">{t.coletasLabel}</span>
                          <span className="kit-coleta-cta">Ver →</span>
                        </div>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
              ) : null}

              {showOkCol ? (
              <div
                className={`kit-col-ok${ok.length === 0 ? ' is-empty' : ''}`}
                data-ok-count={ok.length}
                data-ok-open={okAbertos.length}
              >
                <div className="kit-section-head">
                  <div className="kit-section-title">Kits OK</div>
                  <div className="kit-section-badge kit-section-badge--ok">{ok.length}</div>
                  <div className="kit-section-hint">clique para abrir</div>
                  <div className="kit-section-line" />
                </div>
                <div
                  className="kit-ok-row"
                  style={{
                    '--kit-ok-h': `${alturaOkCard}px`,
                  }}
                >
                  {okColapsados.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="kit-ok-collapsed"
                      onClick={() => toggleOk(t.id)}
                      title={t.nome}
                    >
                      <KitTechFace
                        photoUrl={t.profilePhotoUrl}
                        initials={t.iniciais}
                        tone={t.avatarCor}
                        size="sm"
                      />
                      <div className="kit-ok-name-v">{t.nome}</div>
                      <div className="kit-ok-pct-v">{t.pctLabel}</div>
                      <div className="kit-ok-dot" />
                    </button>
                  ))}
                  {okAbertos.map((t) => (
                    <div key={t.id} className="kit-ok-open">
                      <div className="kit-card-top">
                        <KitTechFace
                          photoUrl={t.profilePhotoUrl}
                          initials={t.iniciais}
                          tone={t.avatarCor}
                        />
                        <div className="kit-card-id">
                          <div className="kit-card-name">{t.nome}</div>
                          <div className="kit-card-sub">{t.linha2}</div>
                        </div>
                        <button
                          type="button"
                          className="kit-link-muted"
                          onClick={() => toggleOk(t.id)}
                        >
                          Recolher
                        </button>
                      </div>
                      <div>
                        <div className="kit-metric-row">
                          <div className="kit-metric-left">
                            <span className="kit-metric-big">{t.emEstoque}</span>
                            <span className="kit-metric-den">/ {t.totalKit}</span>
                            <span className="kit-metric-lbl">peças</span>
                          </div>
                          <div className="kit-metric-pct" style={{ color: '#12a06a' }}>
                            {t.pctLabel}
                          </div>
                        </div>
                        <div className="kit-bar kit-bar--solid">
                          <div
                            className="kit-bar-fill"
                            style={{ width: t.barra, background: '#12a06a' }}
                          />
                        </div>
                      </div>
                      <div className="kit-card-foot">
                        <div className="kit-foot-col">
                          <div className="kit-foot-lbl">Última coleta</div>
                          <div className="kit-foot-val">{t.ultimaColeta}</div>
                        </div>
                        <div className="kit-foot-col">
                          <div className="kit-foot-lbl">Último uso</div>
                          <div className="kit-foot-val">{t.ultimoUso}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="kit-open-full"
                        onClick={() => abrirTech(t.id)}
                      >
                        Abrir Kit completo
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              ) : null}
            </div>
            )}
          </div>
        )}

        {view === 'detalhe' && det && (
          <div className="kit-detail-shell">
            <div className="kit-detail-head">
              <KitTechFace
                photoUrl={det.profilePhotoUrl}
                initials={det.iniciais}
                tone={det.avatarCor}
                size="lg"
              />
              <div className="kit-detail-id">
                <div className="kit-detail-name">{det.nome}</div>
                <div className="kit-detail-sub">
                  {det.linha2}
                  {det.veiculo && det.veiculo !== '—' ? ` · ${det.veiculo}` : ''}
                </div>
              </div>
              <div className="kit-detail-vsep" />
              <div className="kit-detail-metric">
                <div className="kit-metric-left">
                  <span className="kit-metric-big kit-metric-big--md">{det.emEstoque}</span>
                  <span className="kit-metric-den">/ {det.totalKit}</span>
                  <span className="kit-metric-pct" style={{ color: det.sitCor, marginLeft: 6 }}>
                    {det.pctLabel}
                  </span>
                </div>
                <div className="kit-bar" style={{ marginTop: 6 }}>
                  <div className="kit-bar-fill" style={{ width: det.barra, background: det.sitCor }} />
                  <div
                    className="kit-bar-fill"
                    style={{ width: det.barraExcesso, background: det.corExcesso }}
                  />
                </div>
              </div>
              <KitStatusPills
                sitCor={det.sitCor}
                sitLabel={det.sitLabel}
                temSobra={det.temSobra}
                sobraCor={det.sobraCor}
                sobraLabel={det.sobraLabel}
              />
              <div className="kit-flex" />
              <div className="kit-detail-meta">
                <div>
                  <div className="kit-foot-lbl">Última coleta</div>
                  <div className="kit-foot-val kit-foot-val--md">{det.ultimaColeta}</div>
                </div>
                <div>
                  <div className="kit-foot-lbl">Último uso</div>
                  <div className="kit-foot-val kit-foot-val--md">{det.ultimoUso}</div>
                </div>
              </div>
              {qtyDirty ? (
                <button
                  type="button"
                  className="kit-detail-save-btn"
                  disabled={!canSaveQty}
                  title={
                    !canSaveQty && qtyDirty
                      ? increasedProductCodes.size && !allIncreaseStocksOk
                        ? 'Selecione a empresa de estoque nas peças com aumento'
                        : decreasedProductCodes.size && !allDecreaseMotivosOk
                          ? 'Informe o motivo (e empresa, se devolver) nas peças com diminuição'
                          : undefined
                      : undefined
                  }
                  onClick={handleSaveQtyMax}
                >
                  {savingQty ? 'Salvando…' : 'Salvar alterações'}
                </button>
              ) : null}
            </div>

            <div className="kit-detail-tabs-row">
              <div className="kit-detail-tabs" role="tablist">
                {abas.map((a) => {
                  const on = aba === a.key;
                  const movActive = a.key === 'movs' && on;
                  const coletaActive = a.key === 'coletas' && on;
                  const pecasActive = a.key === 'pecas' && on;
                  return (
                    <div
                      key={a.key}
                      className={`kit-detail-tab-cluster${on ? ' is-active' : ''}${
                        movActive || coletaActive ? ' has-period' : ''
                      }${pecasActive ? ' has-add' : ''}`}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={on}
                        className={`kit-detail-tab${on ? ' is-active' : ''}`}
                        onClick={() => setAba(a.key)}
                      >
                        {a.label}
                        <span className="kit-detail-tab-badge">{a.n}</span>
                      </button>
                      {pecasActive ? (
                        <>
                          <span className="kit-detail-tab-add-sep" aria-hidden="true" />
                          <button
                            type="button"
                            className="kit-detail-add-products-btn"
                            aria-label="Adicionar produtos"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddProductsOpen(true);
                            }}
                          >
                            <span className="kit-detail-add-products-btn-icon" aria-hidden>
                              <IconPlus size={13} stroke={2.25} />
                            </span>
                            <span className="kit-detail-add-products-btn-label">Adicionar produtos</span>
                          </button>
                        </>
                      ) : null}
                      {movActive ? (
                        <div className="kit-detail-mov-period">
                          <EcClickUpFilterChip
                            anchorRef={movDayBtnRef}
                            label="Data"
                            value={movPeriodLabel}
                            icon={IconCalendar}
                            hasSelection={movPeriodHasSel}
                            isOpen={movPeriodOpen}
                            className="kit-detail-mov-period-chip"
                            ariaLabel="Filtrar por período"
                            onClick={() => {
                              const rect = movDayBtnRef.current?.getBoundingClientRect();
                              setMovPeriodRect(rect || null);
                              setMovProdOpen(false);
                              setMovOrigemOpen(false);
                              setMovPeriodOpen((v) => !v);
                            }}
                          />
                          {movPeriodOpen ? (
                            <PeriodModal
                              anchorRef={movDayBtnRef}
                              anchorRect={movPeriodRect}
                              currentPeriod={movPeriodHasSel ? movPeriod : null}
                              minDate={movPeriodBounds.minDate}
                              maxDate={movPeriodBounds.maxDate}
                              maxRangeDays={730}
                              hideHeader
                              modalClassName="ec-module-popover"
                              onApply={(startDate, endDate) => setMovPeriod({ startDate, endDate })}
                              onClear={() => setMovPeriod({ startDate: '', endDate: '' })}
                              onClose={() => setMovPeriodOpen(false)}
                            />
                          ) : null}
                          <EcClickUpFilterChip
                            anchorRef={movProdBtnRef}
                            label="Produto"
                            value={movProdChipValue}
                            icon={IconPackages}
                            hasSelection={movProdutosSel.length > 0}
                            isOpen={movProdOpen}
                            className="kit-detail-mov-period-chip"
                            ariaLabel="Filtrar por produto"
                            onClick={() => {
                              setMovPeriodOpen(false);
                              setMovOrigemOpen(false);
                              setMovProdOpen((v) => !v);
                            }}
                          />
                          {movProdOpen && movProdPopoverStyle
                            ? createPortal(
                                <div
                                  data-kit-mov-prod-popover
                                  className={EC_FILTER_POPOVER_CLASS}
                                  style={movProdPopoverStyle}
                                  role="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <BoardSearchSimplePickerPanel
                                    searchPlaceholder="Buscar produto..."
                                    searchTerm={movProdSearch}
                                    onSearchChange={setMovProdSearch}
                                    items={movProductItemsFiltered}
                                    getKey={(item) => item.value}
                                    getLabel={(item) => item.label}
                                    getSuffix={(item) =>
                                      [item.cod, item.ref, item.fab].filter(Boolean).join('   ')
                                    }
                                    selectedKeys={movProdutosSel}
                                    onToggle={(item) =>
                                      setMovProdutosSel((prev) => toggleSelection(prev, item.value))
                                    }
                                    emptyMessage={
                                      kitMovs.length ? 'Nenhum produto encontrado' : 'Nenhuma movimentação'
                                    }
                                    listAriaLabel="Produtos"
                                  />
                                </div>,
                                document.body
                              )
                            : null}
                          <EcClickUpFilterChip
                            anchorRef={movOrigemBtnRef}
                            label="Origem"
                            value={movOrigemChipValue}
                            icon={IconArrowsRightLeft}
                            hasSelection={movOrigemSel.length > 0}
                            isOpen={movOrigemOpen}
                            className="kit-detail-mov-period-chip"
                            ariaLabel="Filtrar por origem ou destino"
                            onClick={() => {
                              setMovPeriodOpen(false);
                              setMovProdOpen(false);
                              setMovOrigemOpen((v) => !v);
                            }}
                          />
                          {movOrigemOpen && movOrigemPopoverStyle
                            ? createPortal(
                                <div
                                  data-kit-mov-origem-popover
                                  className={EC_FILTER_POPOVER_CLASS}
                                  style={movOrigemPopoverStyle}
                                  role="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <BoardSearchSimplePickerPanel
                                    showSearch={false}
                                    items={movOrigemItems}
                                    getKey={(item) => item.value}
                                    getLabel={(item) => item.label}
                                    selectedKeys={movOrigemSel}
                                    onToggle={(item) =>
                                      setMovOrigemSel((prev) => toggleSelection(prev, item.value))
                                    }
                                    emptyMessage={
                                      kitMovs.length ? 'Nenhuma origem encontrada' : 'Nenhuma movimentação'
                                    }
                                    listAriaLabel="Origem ou destino"
                                  />
                                </div>,
                                document.body
                              )
                            : null}
                        </div>
                      ) : null}
                      {coletaActive ? (
                        <div className="kit-detail-mov-period">
                          <EcClickUpFilterChip
                            anchorRef={coletaDayBtnRef}
                            label="Data"
                            value={coletaPeriodLabel}
                            icon={IconCalendar}
                            hasSelection={coletaPeriodHasSel}
                            isOpen={coletaPeriodOpen}
                            className="kit-detail-mov-period-chip"
                            ariaLabel="Filtrar por período"
                            onClick={() => {
                              const rect = coletaDayBtnRef.current?.getBoundingClientRect();
                              setColetaPeriodRect(rect || null);
                              setColetaProdOpen(false);
                              setColetaStatusOpen(false);
                              setColetaPeriodOpen((v) => !v);
                            }}
                          />
                          {coletaPeriodOpen ? (
                            <PeriodModal
                              anchorRef={coletaDayBtnRef}
                              anchorRect={coletaPeriodRect}
                              currentPeriod={coletaPeriodHasSel ? coletaPeriod : null}
                              minDate={coletaPeriodBounds.minDate}
                              maxDate={coletaPeriodBounds.maxDate}
                              maxRangeDays={730}
                              hideHeader
                              modalClassName="ec-module-popover"
                              onApply={(startDate, endDate) => setColetaPeriod({ startDate, endDate })}
                              onClear={() => setColetaPeriod({ startDate: '', endDate: '' })}
                              onClose={() => setColetaPeriodOpen(false)}
                            />
                          ) : null}
                          <EcClickUpFilterChip
                            anchorRef={coletaProdBtnRef}
                            label="Produto"
                            value={coletaProdChipValue}
                            icon={IconPackages}
                            hasSelection={coletaProdutosSel.length > 0}
                            isOpen={coletaProdOpen}
                            className="kit-detail-mov-period-chip"
                            ariaLabel="Filtrar por produto"
                            onClick={() => {
                              setColetaPeriodOpen(false);
                              setColetaStatusOpen(false);
                              setColetaProdOpen((v) => !v);
                            }}
                          />
                          {coletaProdOpen && coletaProdPopoverStyle
                            ? createPortal(
                                <div
                                  data-kit-coleta-prod-popover
                                  className={EC_FILTER_POPOVER_CLASS}
                                  style={coletaProdPopoverStyle}
                                  role="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <BoardSearchSimplePickerPanel
                                    searchPlaceholder="Buscar produto..."
                                    searchTerm={coletaProdSearch}
                                    onSearchChange={setColetaProdSearch}
                                    items={coletaProductItemsFiltered}
                                    getKey={(item) => item.value}
                                    getLabel={(item) => item.label}
                                    getSuffix={(item) =>
                                      [item.cod, item.ref, item.fab].filter(Boolean).join('   ')
                                    }
                                    selectedKeys={coletaProdutosSel}
                                    onToggle={(item) =>
                                      setColetaProdutosSel((prev) => toggleSelection(prev, item.value))
                                    }
                                    emptyMessage={
                                      pendingPedidos.length
                                        ? 'Nenhum produto encontrado'
                                        : 'Nenhum pedido aguardando coleta'
                                    }
                                    listAriaLabel="Produtos"
                                  />
                                </div>,
                                document.body
                              )
                            : null}
                          <EcClickUpFilterChip
                            anchorRef={coletaStatusBtnRef}
                            label="Status"
                            value={coletaStatusChipValue}
                            icon={IconCircleDot}
                            hasSelection={coletaStatusSel.length > 0}
                            isOpen={coletaStatusOpen}
                            className="kit-detail-mov-period-chip"
                            ariaLabel="Filtrar por status"
                            onClick={() => {
                              setColetaPeriodOpen(false);
                              setColetaProdOpen(false);
                              setColetaStatusOpen((v) => !v);
                            }}
                          />
                          {coletaStatusOpen && coletaStatusPopoverStyle
                            ? createPortal(
                                <div
                                  data-kit-coleta-status-popover
                                  className={EC_FILTER_POPOVER_CLASS}
                                  style={coletaStatusPopoverStyle}
                                  role="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <BoardSearchSimplePickerPanel
                                    showSearch={false}
                                    items={coletaStatusItems}
                                    getKey={(item) => item.value}
                                    getLabel={(item) => item.label}
                                    selectedKeys={coletaStatusSel}
                                    onToggle={(item) =>
                                      setColetaStatusSel((prev) => toggleSelection(prev, item.value))
                                    }
                                    emptyMessage={
                                      pendingPedidos.length
                                        ? 'Nenhum status encontrado'
                                        : 'Nenhum pedido aguardando coleta'
                                    }
                                    listAriaLabel="Status"
                                  />
                                </div>,
                                document.body
                              )
                            : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="kit-detail-tabs-search">
                <BoardToolbarExpandSearch
                  value={detailSearch}
                  onChange={setDetailSearch}
                  placeholder="Pesquisar..."
                  ariaLabel="Pesquisar na tabela"
                  forceExpanded
                />
              </div>
            </div>

            {aba === 'pecas' && (
              <div className={`kit-table${showStockCol ? ' kit-table--with-stock' : ''}`}>
                <div
                  className={`kit-table-head kit-table-grid-pecas${
                    showStockCol ? ' kit-table-grid-pecas--stock' : ''
                  }`}
                >
                  <button type="button" className="kit-th-sort" onClick={() => toggleDetailSort('nome')}>
                    Peça{sortInd('nome')}
                  </button>
                  <button type="button" className="kit-th-sort" onClick={() => toggleDetailSort('fab')}>
                    Fabricante{sortInd('fab')}
                  </button>
                  <button type="button" className="kit-th-sort kit-tc" onClick={() => toggleDetailSort('kit')}>
                    Kit{sortInd('kit')}
                  </button>
                  {showStockCol ? (
                    <div className="kit-th-static kit-tc">Empresa</div>
                  ) : null}
                  <button type="button" className="kit-th-sort kit-tc" onClick={() => toggleDetailSort('estoque')}>
                    Estoque{sortInd('estoque')}
                  </button>
                  <button type="button" className="kit-th-sort kit-tc" onClick={() => toggleDetailSort('coleta')}>
                    Pronto p/ coleta{sortInd('coleta')}
                  </button>
                  <button type="button" className="kit-th-sort" onClick={() => toggleDetailSort('saude')}>
                    Saúde{sortInd('saude')}
                  </button>
                </div>
                {kitsLoading ? <KitTableSkeletonRows /> : pecasView.map((p) => {
                  const cod = String(p.cod || '').trim();
                  const savedKit = qtyBaseline[cod] ?? Number(p.kit) ?? 0;
                  const displayKit = resolveDraftKit(cod, savedKit);
                  const isDirty = dirtyProductCodes.has(cod);
                  const isIncrease = increasedProductCodes.has(cod);
                  const isDecrease = decreasedProductCodes.has(cod);
                  return (
                  <div
                    key={p.cod || p.idx}
                    className={`kit-table-row kit-table-grid-pecas${
                      showStockCol ? ' kit-table-grid-pecas--stock' : ''
                    }${isDirty ? ' is-qty-dirty' : ''}`}
                  >
                    <div className="kit-min0">
                      <div className="kit-row-title">{p.nome}</div>
                      <div className="kit-row-meta">
                        <span>{p.cod}</span>
                        <span>{p.ref ? `ref ${p.ref}` : 'ref —'}</span>
                      </div>
                    </div>
                    <div className="kit-fab-cell">{p.fab}</div>
                    <div className={`kit-qty${isDirty ? ' is-dirty' : ''}`}>
                      <button
                        type="button"
                        className="kit-qty-btn"
                        onClick={() => ajustar(p.cod, -1)}
                        aria-label={`Diminuir máximo de ${p.nome}`}
                      >
                        −
                      </button>
                      <div className="kit-qty-n">{displayKit}</div>
                      <button
                        type="button"
                        className="kit-qty-btn"
                        onClick={() => ajustar(p.cod, 1)}
                        aria-label={`Aumentar máximo de ${p.nome}`}
                      >
                        +
                      </button>
                    </div>
                    {showStockCol ? (
                      <div className="kit-stock-cell">
                        {isIncrease ? (
                          <ProductStockPicker
                            productCode={cod}
                            productName={p.nome}
                            selected={stockByProduct[cod] || []}
                            onChange={(next) => setProductStock(cod, next)}
                          />
                        ) : isDecrease ? (
                          <KitQtyMotivoPicker
                            productCode={cod}
                            productName={p.nome}
                            reason={decreaseReasonByProduct[cod]}
                            stockSelected={stockByProduct[cod] || []}
                            onReasonChange={(next) => setProductDecreaseReason(cod, next)}
                            onStockChange={(next) => setProductStock(cod, next)}
                          />
                        ) : (
                          <span className="kit-stock-cell-empty">—</span>
                        )}
                      </div>
                    ) : null}
                    <div className="kit-tc kit-stock" style={{ color: p.cor }}>
                      {p.estoque}
                    </div>
                    <div className="kit-tc">
                      <KitColetaPill qty={p.prontas} pedido={p.pedido} tone={p.coletaCor} />
                    </div>
                    <div className="kit-health">
                      <div className="kit-bar kit-bar--sm">
                        <div className="kit-bar-fill" style={{ width: p.barra, background: p.cor }} />
                        <div
                          className="kit-bar-fill"
                          style={{ width: p.barraExcesso, background: p.corExcesso }}
                        />
                      </div>
                      <div className="kit-health-lbl" style={{ color: p.cor }}>
                        {p.statusLabel}
                      </div>
                    </div>
                  </div>
                  );
                })}
                {!kitsLoading && !pecasView.length ? (
                  <div className="kit-table-empty">Nenhuma peça encontrada</div>
                ) : null}
              </div>
            )}

            {aba === 'movs' && (
              <div className="kit-table">
                <div className={`kit-table-head ${movGridClass}`}>
                  <div className="kit-th-with-filter">
                    <div className="kit-th-static kit-th-static--start">Tipo</div>
                    <button
                      ref={tipoFilterBtnRef}
                      type="button"
                      className={`kit-filter-icon-btn${movTiposSel.length ? ' is-active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTipoFilterOpen((v) => !v);
                      }}
                      aria-label="Filtrar Tipo"
                    >
                      <Funnel size={14} strokeWidth={2} aria-hidden />
                      {movTiposSel.length ? <span className="kit-filter-badge-dot" aria-hidden /> : null}
                    </button>
                  </div>
                  <div className="kit-th-static kit-th-static--start">Peça</div>
                  <div className="kit-th-static kit-tc">Qtde</div>
                  {showMovBalance ? (
                    <div className="kit-th-static kit-tc">Estoque após</div>
                  ) : null}
                  <div className="kit-th-static kit-th-static--start">Origem / destino</div>
                  <div className="kit-th-static kit-tr">Data</div>
                </div>
                {kitMovsLoading ? <KitTableSkeletonRows /> : null}
                {!kitMovsLoading && kitMovsError ? (
                  <div className="kit-table-empty kit-table-empty--error">{kitMovsError}</div>
                ) : null}
                {!kitMovsLoading && !kitMovsError
                  ? movsView.map((m, i) => (
                      <div
                        key={m.id != null ? `mov-${m.id}` : `${m.cod}-${m.data}-${i}`}
                        className={`kit-table-row ${movGridClass}`}
                      >
                        <div
                          className="kit-tipo-pill kit-tone-surface"
                          style={{ '--kit-tone': m.cor, color: m.cor }}
                        >
                          {m.tipo}
                        </div>
                        <div className="kit-min0">
                          <div className="kit-row-title kit-row-title--sm">{m.nome}</div>
                          <div className="kit-row-meta">
                            <span>{m.cod || '—'}</span>
                            <span>{m.ref ? `ref ${m.ref}` : 'ref —'}</span>
                            <span>{m.fab}</span>
                          </div>
                        </div>
                        <div className="kit-tc kit-stock" style={{ color: m.cor }}>
                          {m.qtd}
                        </div>
                        {showMovBalance ? (
                          <div className="kit-tc kit-stock kit-mov-balance">{m.balanceAfter}</div>
                        ) : null}
                        <div className="kit-min0">
                          <div className="kit-mov-l1">{m.linha1}</div>
                          <div className="kit-row-meta">
                            {m.operacao ? <span>{m.operacao}</span> : null}
                            <span>{m.linha2 || '—'}</span>
                            {m.erpQueue ? (
                              <span
                                className="kit-erp-queue kit-tone-surface"
                                style={{ '--kit-tone': m.erpQueue.cor, color: m.erpQueue.cor }}
                                title={m.erpQueue.error || undefined}
                              >
                                {m.erpQueue.label}
                              </span>
                            ) : null}
                            {m.erpQueue?.status === 'failed' ? (
                              <button
                                type="button"
                                className="kit-erp-queue-retry"
                                disabled={erpRetrying === m.erpQueue.id}
                                onClick={() => handleErpRetry(m.erpQueue.id)}
                              >
                                <IconRefresh size={12} stroke={1.8} />
                                Reprocessar
                              </button>
                            ) : null}
                          </div>
                        </div>
                        <div className="kit-tr kit-mov-data">{m.data}</div>
                      </div>
                    ))
                  : null}
                {!kitMovsLoading && !kitMovsError && !movsView.length ? (
                  <div className="kit-table-empty">Nenhuma movimentação encontrada</div>
                ) : null}
              </div>
            )}

            {aba === 'coletas' && (
              <div className="kit-pedidos">
                {pendingPedidosLoading ? <KitPedidosSkeleton /> : null}
                {!pendingPedidosLoading && pendingPedidosError ? (
                  <div className="kit-table-empty kit-table-empty--card kit-table-empty--error">
                    {pendingPedidosError}
                  </div>
                ) : null}
                {!pendingPedidosLoading &&
                  !pendingPedidosError &&
                  pedidosView.map((pd) => (
                  <div key={pd.numero} className="kit-pedido">
                    <div className="kit-pedido-head">
                      <div className="kit-pedido-dot" style={{ background: pd.cor }} />
                      <div className="kit-pedido-num">{pd.numero}</div>
                      <div
                        className="kit-pedido-st kit-tone-surface"
                        style={{ '--kit-tone': pd.cor, color: pd.cor }}
                      >
                        {pd.status}
                      </div>
                      <div className="kit-flex" />
                      {pd.local ? <div className="kit-pedido-local">{pd.local}</div> : null}
                    </div>
                    {(pd.itens || []).map((i, idx) => (
                      <div key={`${pd.numero}-${i.cod || 'i'}-${idx}`} className="kit-pedido-item">
                        <div className="kit-min0 kit-flex-1">
                          <div className="kit-row-title kit-row-title--sm">{i.nome}</div>
                          <div className="kit-row-meta">
                            {i.cod ? <span>{i.cod}</span> : null}
                            {i.ref ? <span>ref {i.ref}</span> : null}
                            {i.fab && i.fab !== '—' ? <span>{i.fab}</span> : null}
                          </div>
                        </div>
                        <div className="kit-pedido-qtd">{i.qtd}</div>
                      </div>
                    ))}
                    <div className="kit-pedido-foot">
                      <div className="kit-pedido-date">
                        {pd.liberado && pd.data
                          ? `Liberado em ${pd.data}`
                          : pd.data
                            ? pd.data
                            : 'Aguardando liberação'}
                      </div>
                    </div>
                  </div>
                ))}
                {!pendingPedidosLoading && !pendingPedidosError && !pedidosView.length ? (
                  <div className="kit-table-empty kit-table-empty--card">Nenhum pedido encontrado</div>
                ) : null}
              </div>
            )}

            <PermColumnFilterPopover
              anchorRef={tipoFilterBtnRef}
              isOpen={tipoFilterOpen && aba === 'movs'}
              onClose={() => setTipoFilterOpen(false)}
              options={movTipoOptions}
              selected={movTiposSel}
              onApply={setMovTiposSel}
              listAriaLabel="Filtrar Tipo"
              showSearch={false}
            />
          </div>
        )}

        <KitCreateKitModal
          open={modal}
          onClose={() => setModal(false)}
          onCreated={handleKitsCreated}
        />
        <KitCreateKitModal
          open={addProductsOpen}
          mode="add"
          tech={det}
          excludeProductCodes={(det?.itens || []).map((p) => p.cod || p.productCode).filter(Boolean)}
          onClose={() => setAddProductsOpen(false)}
          onCreated={handleProductsAdded}
        />
      </div>
    </div>
    {savePhase !== 'idle'
      ? createPortal(
          <div
            className={`kit-save-result-portal kit-create-result-overlay kit-create-result-overlay--${
              savePhase === 'saving' ? 'creating' : savePhase
            }`}
            role="status"
            aria-live="polite"
            aria-busy={savePhase === 'saving'}
          >
            <div className="kit-create-result-card">
              {savePhase === 'saving' ? (
                <>
                  <div className="kit-create-result-spinner" aria-hidden />
                  <p className="kit-create-result-title">Salvando alterações…</p>
                  <p className="kit-create-result-text">
                    Atualizando quantidades
                    {increasedProductCodes.size ? ' e gerando pedido de reposição' : ''}
                    {decreasedProductCodes.size ? ' e gerando rollback de entrada' : ''}
                    .
                  </p>
                </>
              ) : null}
              {savePhase === 'success' ? (
                <>
                  <div className="kit-create-result-icon kit-create-result-icon--ok" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12.5l4.2 4.2L19 7"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="kit-create-result-title">Alterações salvas</p>
                  <p className="kit-create-result-text">{saveMessage}</p>
                  {savePedidos.some((p) => String(p?.pedidoGerado || '').trim()) ? (
                    <ul className="kit-create-result-list kit-create-result-list--ok" aria-label="Documentos gerados">
                      {savePedidos.map((p, idx) => {
                        const num = String(p?.pedidoGerado || '').trim();
                        if (!num) return null;
                        const emp = String(p?.codEmp || '').trim();
                        const isEntrada = p?.kind === 'entrada';
                        return (
                          <li key={`${num}-${emp}-${idx}`}>
                            <span className="kit-create-result-list-code">{num}</span>
                            {isEntrada || emp ? (
                              <span className="kit-create-result-list-meta">
                                {isEntrada ? 'Entrada' : ''}
                                {isEntrada && emp ? ' · ' : ''}
                                {emp ? `Emp. ${emp}` : ''}
                              </span>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn kit-create-result-btn"
                    onClick={dismissSaveResult}
                    autoFocus
                  >
                    Concluir
                  </button>
                </>
              ) : null}
              {savePhase === 'error' ? (
                <>
                  <div className="kit-create-result-icon kit-create-result-icon--err" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 8v5.5M12 16.5h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <p className="kit-create-result-title">Não foi possível salvar</p>
                  <p className="kit-create-result-text">{saveMessage}</p>
                  {saveErrors.length > 0 ? (
                    <ul className="kit-create-result-list kit-create-result-list--err" aria-label="Erros">
                      {saveErrors.map((e, idx) => {
                        const code = e?.number != null ? String(e.number) : '';
                        const msg = String(e?.message || '').trim();
                        const emp = String(e?.codEmp || '').trim();
                        const proc = String(e?.procedure || '').trim();
                        return (
                          <li key={`${code}-${idx}`}>
                            {code ? (
                              <span className="kit-create-result-list-code">#{code}</span>
                            ) : null}
                            <span className="kit-create-result-list-msg">
                              {msg || 'Erro na procedure'}
                            </span>
                            {(emp || proc) && (
                              <span className="kit-create-result-list-meta">
                                {[emp ? `Emp. ${emp}` : null, proc || null]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn kit-create-result-btn"
                    onClick={dismissSaveResult}
                    autoFocus
                  >
                    Entendi
                  </button>
                </>
              ) : null}
            </div>
          </div>,
          document.body
        )
      : null}
    {Toaster}
    </>
  );
};

export default KitTecnicoPage;
